import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { filter, map, race, take, timer } from 'rxjs';

/**
 * Auth guard that preserves the attempted URL as a `returnUrl` query param.
 *
 * The vendored `@freesixtyfive/auth` `AuthGuard` redirects to the login path
 * without carrying the attempted URL, so an expired session loses where the
 * user was heading. This NFC check-in flow must come back to the exact
 * `control-points/checkin/:tag` URL after login so the POST auto-fires with no
 * second tap. This guard mirrors the vendored guard's authenticated check but,
 * when unauthenticated, redirects to `/login?returnUrl=<attempted-url>`.
 *
 * The Sanctum token resolves asynchronously on a cold load, so we wait briefly
 * for `isAuth` to settle (matching the `permissionGuard` `toObservable` pattern)
 * before deciding to redirect.
 */
export const authReturnGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const auth = inject(SanctumAuthService);
  const router = inject(Router);

  const redirect = () =>
    router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });

  if (auth.isAuth()) {
    return true;
  }

  // Token may still be resolving from storage on a cold load. Wait briefly for
  // `isAuth` to flip to true; if it has not settled within the window, treat the
  // session as expired and redirect to login carrying the attempted URL.
  return race(
    toObservable(auth.isAuth).pipe(
      filter(Boolean),
      take(1),
      map(() => true as const),
    ),
    timer(750).pipe(map(() => redirect())),
  );
};
